import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { AdminService } from "Services/adminService";
import { ExamServices } from "Services/examServices";
import TablePagination from "components/Pagination/Table.paginaition";

interface Transaction {
  _id: string;
  method: string;
  currency: string;
  status: string;
  user?: {
    _id: string;
    name: string;
    last_name?: string;
    email: string;
    registrationNumber?: string;
  };
  exam?: {
    _id: string;
    examName: string;
  };
  mappingId?: string;
  registerationNumber?: string;
  orderId?: string;
  paymentId?: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "completed", label: "Completed" },
  { value: "expired", label: "Expired" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
];

const statusVariant = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "expired":
      return "secondary";
    case "failed":
      return "danger";
    default:
      return "light";
  }
};

export default function Transactions() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [examFilter, setExamFilter] = useState("");
  const [exams, setExams] = useState<{ value: string; label: string }[]>([]);

  const getAllTransactions = async () => {
    try {
      const res = await AdminService.getAllTransactions(
        examFilter,
        search,
        perPage,
        page
      );
      if (res?.status === 200) {
        const filtered = statusFilter
          ? res.data.data.transactions.filter((t: Transaction) => t.status === statusFilter)
          : res.data.data.transactions;
        setTransactions(filtered);
        setTotal(res.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions");
    }
  };

  const getAllExams = async () => {
    try {
      const res = await ExamServices.getAllExamsLean();
      if (res?.status === 200 && res.data.data?.examData?.length > 0) {
        setExams(
          res.data.data.examData.map((item: any) => ({
            value: item._id,
            label: item.examName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching exams:", error);
    }
  };

  useEffect(() => {
    getAllExams();
  }, []);

  useEffect(() => {
    getAllTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, examFilter, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      getAllTransactions();
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <Container fluid>
      <div className="py-4">
        <div className="mb-4">
          <h5 className="mb-1 fw-bold">Transactions</h5>
          <span className="text-muted fs-12">
            Payment records · {total} total
          </span>
        </div>

        <Row className="mb-3 g-2 align-items-center">
          <Col md={4}>
            <Form.Control
              type="text"
              placeholder="Search by name, email, order id..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Select
              options={exams}
              isClearable
              placeholder="All Exams"
              value={exams.find((e) => e.value === examFilter) || null}
              onChange={(e: any) => {
                setExamFilter(e?.value || "");
                setPage(1);
              }}
            />
          </Col>
          <Col md={3}>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Card className="xrg-card xrg-table-card shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="xrg-table mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Reg. No.</th>
                  <th>Exam</th>
                  <th>Amount</th>
                  <th className="text-center">Status</th>
                  <th>Date</th>
                  <th className="text-center" style={{ width: 80 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.length > 0 ? (
                  transactions?.map((txn, index) => (
                    <tr
                      key={txn._id}
                      className="xrg-table-row"
                      onClick={() =>
                        navigate("/admin-dash/transactions/" + txn._id)
                      }
                    >
                      <td className="text-muted">
                        {(page - 1) * perPage + index + 1}
                      </td>
                      <td className="fw-semibold">
                        {`${txn.user?.name || ""} ${txn.user?.last_name || ""}`.trim() || "-"}
                      </td>
                      <td>{txn.user?.email || "-"}</td>
                      <td>
                        {txn.user?.registrationNumber || txn.registerationNumber || "-"}
                      </td>
                      <td
                        className="text-truncate"
                        style={{ maxWidth: 220 }}
                        title={txn.exam?.examName}
                      >
                        {txn.exam?.examName || "-"}
                      </td>
                      <td className="fw-semibold">
                        {txn.amount != null
                          ? `${txn.currency || ""} ${txn.amount}`.trim()
                          : "-"}
                      </td>
                      <td className="text-center">
                        <Badge bg={statusVariant(txn.status)} className="text-capitalize">
                          {txn.status}
                        </Badge>
                      </td>
                      <td className="text-muted">
                        {new Date(txn.createdAt).toLocaleString()}
                      </td>
                      <td
                        className="text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FontAwesomeIcon
                          icon={faEye}
                          role="button"
                          title="View details"
                          className="text-primary cursor-pointer px-2"
                          onClick={() =>
                            navigate("/admin-dash/transactions/" + txn._id)
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      No transactions found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {total > 0 && (
          <div className="mt-3">
            <TablePagination
              total={total}
              currentPage={page}
              perPage={perPage}
              handlePageChange={setPage}
              setPerPage={setPerPage}
            />
          </div>
        )}
      </div>
    </Container>
  );
}
